import { useRef, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Panel, DefaultButton, Modal, IconButton, Icon } from "@fluentui/react";
import readNDJSONStream from "ndjson-readablestream";

import appLogo from "../../assets/applogo.svg";
import sendIcon from "../../assets/SendIcon.png";
import welcomeIcon from "../../assets/welcome.png";
import doubleChevronLeft from "../../assets/DoubleChevronLeft.svg";
import styles from "./Chat.module.css";

import {
    chatApi,
    configApi,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ResponseMessage,
    VectorFields,
    GPT4VInput,
    SpeechConfig
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList, ExampleFilters } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { HistoryPanel } from "../../components/HistoryPanel";
import { HistoryProviderOptions, useHistoryManager } from "../../components/HistoryProviders";
import { HistoryButton } from "../../components/HistoryButton";
import { UploadFile } from "../../components/UploadFile";
import { useLogin, getToken, requireAccessControl } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { LoginContext } from "../../loginContext";
import { LanguagePicker } from "../../i18n/LanguagePicker";
import { ChevronDown16Filled } from "@fluentui/react-icons";
// import { Settings } from "../../components/Settings/Settings";
import {
    Filters,
    regionOptions,
    regionalBureauOptions,
    countryOptions,
    evaluationTypeOptions,
    qaRatingOptions,
    sectionTypeOptions,
    gefEvaluationOptions,
    jointEvaluationOptions,
    yearOptions
} from "../../components/Filter/Filters";

const Chat = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
    const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [seed, setSeed] = useState<number | null>(null);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(20);
    const [maxSubqueryCount, setMaxSubqueryCount] = useState<number>(10);
    const [resultsMergeStrategy, setResultsMergeStrategy] = useState<string>("interleaved");
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useQueryRewriting, setUseQueryRewriting] = useState<boolean>(false);
    const [reasoningEffort, setReasoningEffort] = useState<string>("");
    const [streamingEnabled, setStreamingEnabled] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(true);
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const [includeCategory, setIncludeCategory] = useState<string>("");
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [vectorFields, setVectorFields] = useState<VectorFields>(VectorFields.TextAndImageEmbeddings);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [useGPT4V, setUseGPT4V] = useState<boolean>(false);

    // Filters state
    const [country, setCountry] = useState<string[]>([]);
    const [region, setRegion] = useState<string[]>([]);
    const [regionalBureau, setRegionalBureau] = useState<string[]>([]);
    const [evaluationType, setEvaluationType] = useState<string[]>([]);
    const [qarating, setQaRating] = useState<string[]>([]);
    const [sectionTypes, setSectionTypes] = useState<string[]>([]);
    const [projectType, setProjectType] = useState<string>("");

    const [sdgGoals, setSdgGoals] = useState<string>("");
    const [jointEvaluation, setJointEvaluation] = useState<string>("");
    const [gefEvaluation, setGefEvaluation] = useState<string>("");
    const [yearFrom, setYearFrom] = useState<string>("");
    const [yearTo, setYearTo] = useState<string>("");

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [oldAnswers, setOldAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);

    // Track previous filters used in API calls to detect changes
    const [previousFilters, setPreviousFilters] = useState<{
        country: string[];
        region: string[];
        regionalBureau: string[];
        evaluationType: string[];
        qarating: string[];
        sectionTypes: string[];
        projectType: string;
        sdgGoals: string;
        jointEvaluation: string;
        gefEvaluation: string;
        yearFrom: string;
        yearTo: string;
    } | null>(null);
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);

    const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showQueryRewritingOption, setShowQueryRewritingOption] = useState<boolean>(false);
    const [showReasoningEffortOption, setShowReasoningEffortOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
    const [showLanguagePicker, setshowLanguagePicker] = useState<boolean>(false);
    const [showSpeechInput, setShowSpeechInput] = useState<boolean>(false);
    const [showSpeechOutputBrowser, setShowSpeechOutputBrowser] = useState<boolean>(false);
    const [showSpeechOutputAzure, setShowSpeechOutputAzure] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);
    const [showAgenticRetrievalOption, setShowAgenticRetrievalOption] = useState<boolean>(false);
    const [useAgenticRetrieval, setUseAgenticRetrieval] = useState<boolean>(false);
    const [filterScrollTarget, setFilterScrollTarget] = useState<string | undefined>(undefined);
    const [activeFilterBar, setActiveFilterBar] = useState<
        "region" | "regionalBureau" | "country" | "evaluationType" | "qarating" | "sectionTypes" | "gefEvaluation" | "jointEvaluation" | "yearFrom" | "yearTo" | null
    >(null);
    const [filterSearchText, setFilterSearchText] = useState("");
    const filterSearchInputRef = useRef<HTMLInputElement>(null);
    const [mobileFiltersExpanded, setMobileFiltersExpanded] = useState(false);
    const [moreFiltersExpanded, setMoreFiltersExpanded] = useState(false);
    const [mobileShowPanel, setMobileShowPanel] = useState(true);

    // Auto-show panel on mobile when a tab is activated
    useEffect(() => {
        if (activeAnalysisPanelTab) {
            setMobileShowPanel(true);
        }
    }, [activeAnalysisPanelTab]);

    useEffect(() => {
        setFilterSearchText("");
        if (activeFilterBar && (activeFilterBar === "country" || activeFilterBar === "regionalBureau")) {
            setTimeout(() => filterSearchInputRef.current?.focus(), 50);
        }
    }, [activeFilterBar]);

    const activeFilterCount =
        region.length +
        regionalBureau.length +
        country.length +
        evaluationType.length +
        qarating.length +
        sectionTypes.length +
        (gefEvaluation ? 1 : 0) +
        (jointEvaluation ? 1 : 0) +
        (yearFrom ? 1 : 0) +
        (yearTo ? 1 : 0);

    const hasMoreFiltersActive = gefEvaluation !== "" || jointEvaluation !== "" || qarating.length > 0;
    const isMoreFilterOpen = activeFilterBar === "gefEvaluation" || activeFilterBar === "jointEvaluation" || activeFilterBar === "qarating";

    const audio = useRef(new Audio()).current;
    const [isPlaying, setIsPlaying] = useState(false);

    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const filterBarRef = useRef<HTMLDivElement | null>(null);

    const [showChatScrollButton, setShowChatScrollButton] = useState(false);
    const [showPanelScrollButton, setShowPanelScrollButton] = useState(false);

    const speechConfig: SpeechConfig = {
        speechUrls,
        setSpeechUrls,
        audio,
        isPlaying,
        setIsPlaying
    };

    const getConfig = async () => {
        configApi().then(config => {
            setShowGPT4VOptions(config.showGPT4VOptions);
            if (config.showGPT4VOptions) {
                setUseGPT4V(true);
            }
            setUseSemanticRanker(config.showSemanticRankerOption);
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setUseQueryRewriting(config.showQueryRewritingOption);
            setShowQueryRewritingOption(config.showQueryRewritingOption);
            setShowReasoningEffortOption(config.showReasoningEffortOption);
            setStreamingEnabled(config.streamingEnabled);
            if (!config.streamingEnabled) {
                setShouldStream(false);
            }
            if (config.showReasoningEffortOption) {
                setReasoningEffort(config.defaultReasoningEffort);
            }
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
            setShowUserUpload(config.showUserUpload);
            setshowLanguagePicker(config.showLanguagePicker);
            setShowSpeechInput(config.showSpeechInput);
            setShowSpeechOutputBrowser(config.showSpeechOutputBrowser);
            setShowSpeechOutputAzure(config.showSpeechOutputAzure);
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
            setShowAgenticRetrievalOption(config.showAgenticRetrievalOption);
            setUseAgenticRetrieval(config.showAgenticRetrievalOption);
            if (config.showAgenticRetrievalOption) {
                setRetrieveCount(10);
            }
        });
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        message: { content: answer, role: askResponse.message.role }
                    };
                    setStreamedAnswers([...oldAnswers, ...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (event["context"] && event["context"]["data_points"]) {
                    event["message"] = event["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["delta"] && event["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["delta"]["content"]);
                } else if (event["context"]) {
                    // Update context with new keys from latest event
                    askResponse.context = { ...askResponse.context, ...event["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: { content: answer, role: askResponse.message.role }
        };
        return fullResponse;
    };

    // Function to compare current filters with previous filters
    const areFiltersChanged = () => {
        if (!previousFilters) return true; // First time, consider as changed

        const currentFilters = {
            country,
            region,
            regionalBureau,
            evaluationType,
            qarating,
            sectionTypes,
            projectType,
            sdgGoals,
            jointEvaluation,
            gefEvaluation,
            yearFrom,
            yearTo
        };

        // Deep comparison of filter arrays and strings
        return (
            JSON.stringify(currentFilters.country.sort()) !== JSON.stringify(previousFilters.country.sort()) ||
            JSON.stringify(currentFilters.region.sort()) !== JSON.stringify(previousFilters.region.sort()) ||
            JSON.stringify(currentFilters.evaluationType.sort()) !== JSON.stringify(previousFilters.evaluationType.sort()) ||
            JSON.stringify(currentFilters.qarating.sort()) !== JSON.stringify(previousFilters.qarating.sort()) ||
            JSON.stringify(currentFilters.sectionTypes.sort()) !== JSON.stringify(previousFilters.sectionTypes.sort()) ||
            currentFilters.projectType !== previousFilters.projectType ||
            currentFilters.sdgGoals !== previousFilters.sdgGoals ||
            currentFilters.jointEvaluation !== previousFilters.jointEvaluation ||
            currentFilters.gefEvaluation !== previousFilters.gefEvaluation ||
            currentFilters.yearFrom !== previousFilters.yearFrom ||
            currentFilters.yearTo !== previousFilters.yearTo
        );
    };

    const client = useLogin ? useMsal().instance : undefined;
    const { loggedIn } = useContext(LoginContext);

    const historyProvider: HistoryProviderOptions = (() => {
        if (useLogin && showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();
    const historyManager = useHistoryManager(historyProvider);

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        // Check if filters have changed compared to previous API call
        const filtersHaveChanged = areFiltersChanged();

        // If filters have changed and there are existing answers, move them to oldAnswers
        let currentAnswers = answers;
        if (filtersHaveChanged && answers.length > 0) {
            console.log("Filters have changed, moving answers to oldAnswers");
            setOldAnswers(prevOldAnswers => [...prevOldAnswers, ...answers]);
            setAnswers([]);
            currentAnswers = []; // Use empty array for this API call
        }

        // Update previous filters for next comparison
        setPreviousFilters({
            country,
            region,
            regionalBureau,
            evaluationType,
            qarating,
            sectionTypes,
            projectType,
            sdgGoals,
            jointEvaluation,
            gefEvaluation,
            yearFrom,
            yearTo
        });

        const token = client ? await getToken(client) : undefined;

        try {
            console.log("Making API request with answers:", currentAnswers);
            const messages: ResponseMessage[] = currentAnswers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].message.content, role: "assistant" }
            ]);

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        include_category: includeCategory.length === 0 ? undefined : includeCategory,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        max_subqueries: maxSubqueryCount,
                        results_merge_strategy: resultsMergeStrategy,
                        temperature: temperature,
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        query_rewriting: useQueryRewriting,
                        reasoning_effort: reasoningEffort,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        vector_fields: vectorFields,
                        use_gpt4v: useGPT4V,
                        gpt4v_input: gpt4vInput,
                        language: i18n.language,
                        use_agentic_knowledgebase: showAgenticRetrievalOption,
                        ...(seed !== null ? { seed: seed } : {})
                    },
                    filters: {
                        country: country,
                        region: region,
                        regionalBureau: regionalBureau,
                        evaluationType: evaluationType,
                        qarating: qarating,
                        sectionTypes: sectionTypes,
                        projectType: projectType,
                        sdgGoals: sdgGoals,
                        jointEvaluation: jointEvaluation,
                        gefEvaluation: gefEvaluation,
                        yearFrom: yearFrom,
                        yearTo: yearTo
                    }
                },
                // AI Chat Protocol: Client must pass on any session state received from the server
                session_state: currentAnswers.length ? currentAnswers[currentAnswers.length - 1][1].session_state : null
            };

            const response = await chatApi(request, shouldStream, token);
            if (!response.body) {
                throw Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                throw Error(`Request failed with status ${response.status}`);
            }
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(
                    question,
                    filtersHaveChanged ? [...answers, ...currentAnswers] : currentAnswers,
                    response.body
                );
                setAnswers([...currentAnswers, [question, parsedResponse]]);
                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    const token = client ? await getToken(client) : undefined;
                    historyManager.addItem(parsedResponse.session_state, [...currentAnswers, [question, parsedResponse]], token);
                }
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (parsedResponse.error) {
                    throw Error(parsedResponse.error);
                }
                setAnswers([...currentAnswers, [question, parsedResponse as ChatAppResponse]]);
                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    const token = client ? await getToken(client) : undefined;
                    historyManager.addItem(parsedResponse.session_state, [...currentAnswers, [question, parsedResponse as ChatAppResponse]], token);
                }
            }
            setSpeechUrls([...speechUrls, null]);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setOldAnswers([]);
        setSpeechUrls([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        setPreviousFilters(null); // Reset previous filters tracking
    };

    useEffect(() => {
        const handleHomeEvent = () => {
            clearChat();
        };
        window.addEventListener("aida:home", handleHomeEvent);
        return () => window.removeEventListener("aida:home", handleHomeEvent);
    }, []);

    // Scroll to bottom function
    const scrollToBottom = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    // Smart auto-scroll: only scroll if user is near bottom during streaming
    useEffect(() => {
        if (streamedAnswers.length > 0 && chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

            if (isNearBottom) {
                chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
            }
        }
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, [answers]);

    // Check if this is the first time ever and show welcome modal
    useEffect(() => {
        const hasSeenWelcome = localStorage.getItem("aida_welcome_seen");
        if (!hasSeenWelcome) {
            setIsWelcomeModalOpen(true);
        }
    }, []);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterBarRef.current && !filterBarRef.current.contains(event.target as Node)) {
                setActiveFilterBar(null);
            }
        };

        if (activeFilterBar) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [activeFilterBar]);

    // Auto-scroll to show filter dropdown when opened (only when panel is open)
    useEffect(() => {
        if (activeFilterBar && activeAnalysisPanelTab && filterBarRef.current) {
            // Small delay to ensure dropdown is rendered
            setTimeout(() => {
                filterBarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 50);
        }
    }, [activeFilterBar, activeAnalysisPanelTab]);

    // Prevent browser scrollbar when supporting content panel is open on large screens
    useEffect(() => {
        if (activeAnalysisPanelTab && window.innerWidth >= 1200) {
            document.documentElement.style.overflow = "hidden";
            document.body.style.overflow = "hidden";
        } else {
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
        }
        return () => {
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
        };
    }, [activeAnalysisPanelTab]);

    // Track chat container scroll position to show/hide scroll-to-bottom button
    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 150;
            setShowChatScrollButton(!isNearBottom);
        };
        handleScroll();
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [lastQuestionRef.current, answers, oldAnswers, streamedAnswers, isLoading]);

    // Track analysis panel scroll position to show/hide scroll-to-bottom button
    useEffect(() => {
        const panel = document.querySelector(`.${styles.chatAnalysisPanel}`) as HTMLElement | null;
        if (!panel) {
            setShowPanelScrollButton(false);
            return;
        }
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = panel;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 150;
            setShowPanelScrollButton(!isNearBottom);
        };
        handleScroll();
        panel.addEventListener("scroll", handleScroll);
        return () => panel.removeEventListener("scroll", handleScroll);
    }, [activeAnalysisPanelTab, selectedAnswer, answers, oldAnswers, mobileShowPanel]);

    const handleFiltersChange = (field: string, value: any) => {
        switch (field) {
            case "country":
                setCountry(value);
                break;
            case "region":
                setRegion(value);
                break;
            case "regionalBureau":
                setRegionalBureau(value);
                break;
            case "evaluationType":
                setEvaluationType(value);
                break;
            case "qarating":
                setQaRating(value);
                break;
            case "sectionTypes":
                setSectionTypes(value);
                break;
            case "projectType":
                setProjectType(value);
                break;
            case "sdgGoals":
                setSdgGoals(value);
                break;
            case "jointEvaluation":
                setJointEvaluation(value);
                break;
            case "gefEvaluation":
                setGefEvaluation(value);
                break;
            case "yearFrom":
                setYearFrom(value);
                break;
            case "yearTo":
                setYearTo(value);
                break;
        }
    };

    const handleFiltersReset = () => {
        setCountry([]);
        setRegion([]);
        setRegionalBureau([]);
        setEvaluationType([]);
        setQaRating([]);
        setSectionTypes([]);
        setProjectType("");
        setSdgGoals("");
        setJointEvaluation("");
        setGefEvaluation("");
        setYearFrom("");
        setYearTo("");
        setMoreFiltersExpanded(false);
    };

    const handleRemoveFilter = (field: string, value?: string) => {
        switch (field) {
            case "country":
                if (value) {
                    setCountry(prev => prev.filter(c => c !== value));
                } else {
                    setCountry([]);
                }
                break;
            case "region":
                if (value) {
                    setRegion(prev => prev.filter(r => r !== value));
                } else {
                    setRegion([]);
                }
                break;
            case "regionalBureau":
                if (value) {
                    setRegionalBureau(prev => prev.filter(r => r !== value));
                } else {
                    setRegionalBureau([]);
                }
                break;
            case "evaluationType":
                if (value) {
                    setEvaluationType(prev => prev.filter(e => e !== value));
                } else {
                    setEvaluationType([]);
                }
                break;
            case "qaRating":
                if (value) {
                    setQaRating(prev => prev.filter(q => q !== value));
                } else {
                    setQaRating([]);
                }
                break;
            case "sectionTypes":
                if (value) {
                    setSectionTypes(prev => prev.filter(s => s !== value));
                } else {
                    setSectionTypes([]);
                }
                break;
            case "projectType":
                setProjectType("");
                break;
            case "sdgGoals":
                setSdgGoals("");
                break;
            case "jointEvaluation":
                setJointEvaluation("");
                break;
            case "gefEvaluation":
                setGefEvaluation("");
                break;
            case "yearFrom":
                setYearFrom("");
                break;
            case "yearTo":
                setYearTo("");
                break;
        }
    };

    // Example clicked - apply filters and make request
    const onExampleClicked = (example: string, filters?: ExampleFilters) => {
        // Apply filters if provided
        if (filters) {
            if (filters.country) setCountry(filters.country);
            if (filters.region) setRegion(filters.region);
            if (filters.regionalBureau) setRegionalBureau(filters.regionalBureau);
            if (filters.evaluationType) setEvaluationType(filters.evaluationType);
            if (filters.qarating) setQaRating(filters.qarating);
            if (filters.gefEvaluation) setGefEvaluation(filters.gefEvaluation);
            if (filters.yearFrom) setYearFrom(filters.yearFrom);
            if (filters.yearTo) setYearTo(filters.yearTo);
            // Delay API request to allow state updates to process
            setTimeout(() => makeApiRequest(example), 0);
        } else {
            makeApiRequest(example);
        }
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const handleWelcomeModalClose = () => {
        localStorage.setItem("aida_welcome_seen", "true");
        setIsWelcomeModalOpen(false);
    };

    const { t, i18n } = useTranslation();

    const activeFilters = [
        country.length ? `Country: ${country.join(", ")}` : null,
        region.length ? `Region: ${region.join(", ")}` : null,
        regionalBureau.length ? `Regional Bureau: ${regionalBureau.join(", ")}` : null,
        evaluationType.length ? `Evaluation Type: ${evaluationType.join(", ")}` : null,
        qarating.length ? `QA Rating: ${qarating.join(", ")}` : null,
        sectionTypes.length ? `Section: ${sectionTypes.join(", ")}` : null,
        projectType ? `Project Type: ${projectType}` : null,
        sdgGoals ? `SDG Goals: ${sdgGoals}` : null,
        jointEvaluation ? `Joint Evaluation: ${jointEvaluation}` : null,
        gefEvaluation ? `GEF Evaluation: ${gefEvaluation}` : null,
        yearFrom || yearTo ? `Year: ${yearFrom || ""}${yearFrom && yearTo ? "–" : ""}${yearTo || ""}` : null
    ].filter(Boolean) as string[];

    const filterOptionsMap = {
        region: regionOptions,
        regionalBureau: regionalBureauOptions,
        country: countryOptions,
        evaluationType: evaluationTypeOptions,
        qarating: qaRatingOptions,
        sectionTypes: sectionTypeOptions,
        gefEvaluation: gefEvaluationOptions,
        jointEvaluation: jointEvaluationOptions,
        yearFrom: yearOptions,
        yearTo: yearOptions
    } as const;

    const getCurrentFilterValue = (key: keyof typeof filterOptionsMap) => {
        switch (key) {
            case "region":
                return region;
            case "regionalBureau":
                return regionalBureau;
            case "country":
                return country;
            case "evaluationType":
                return evaluationType;
            case "qarating":
                return qarating;
            case "sectionTypes":
                return sectionTypes;
            case "gefEvaluation":
                return gefEvaluation;
            case "jointEvaluation":
                return jointEvaluation;
            case "yearFrom":
                return yearFrom;
            case "yearTo":
                return yearTo;
        }
    };

    const getFilteredOptions = (filterKey: keyof typeof filterOptionsMap) => {
        const options = filterOptionsMap[filterKey];
        if ((filterKey === "country" || filterKey === "regionalBureau") && filterSearchText) {
            return options.filter(o => o.text.toLowerCase().includes(filterSearchText.toLowerCase()));
        }
        return options;
    };

    const updateFilterValue = (key: keyof typeof filterOptionsMap, optionKey: string) => {
        const current = getCurrentFilterValue(key);
        if (Array.isArray(current)) {
            const exists = current.includes(optionKey);
            const next = exists ? current.filter(v => v !== optionKey) : [...current, optionKey];
            handleFiltersChange(key, next);
        } else {
            // Toggle behavior for single-value filters: click again to deselect
            const newValue = current === optionKey ? "" : optionKey;
            handleFiltersChange(key, newValue);
        }
    };

    const renderInlineDropdown = (filterKey: keyof typeof filterOptionsMap) => {
        if (activeFilterBar !== filterKey) return null;
        const hasSearch = filterKey === "country" || filterKey === "regionalBureau";
        const options = getFilteredOptions(filterKey);
        return (
            <div
                className={`${styles.filterDropdownInline} ${
                    filterKey === "region" || filterKey === "evaluationType" || filterKey === "qarating"
                        ? styles.filterDropdown2Col
                        : filterKey === "sectionTypes"
                          ? styles.filterDropdown2Col
                          : filterKey === "gefEvaluation" || filterKey === "jointEvaluation"
                            ? styles.filterDropdown1Col
                            : filterKey === "yearFrom" || filterKey === "yearTo"
                              ? styles.filterDropdownYearCol
                              : styles.filterDropdown3Col
                }`}
            >
                {hasSearch && (
                    <div className={styles.filterSearchWrapper}>
                        <input
                            ref={filterSearchInputRef}
                            type="text"
                            className={styles.filterSearchInput}
                            placeholder={`Search ${filterKey === "country" ? "country" : "programme unit"}...`}
                            value={filterSearchText}
                            onChange={e => setFilterSearchText(e.target.value)}
                        />
                    </div>
                )}
                {options.map(option => {
                    const current = getCurrentFilterValue(filterKey);
                    const isSelected = Array.isArray(current) ? current.includes(option.key as string) : current === option.key;
                    return (
                        <button
                            key={option.key as string}
                            type="button"
                            className={`${styles.filterDropdownItem} ${isSelected ? styles.filterDropdownItemSelected : ""}`}
                            onClick={() => updateFilterValue(filterKey, option.key as string)}
                        >
                            <span className={styles.filterDropdownItemCircle}></span>
                            <span className={styles.filterDropdownItemText} title={option.text}>
                                {option.text}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.commandsContainer}>{showUserUpload && <UploadFile className={styles.commandButton} disabled={!loggedIn} />}</div>
            <main className={(answers.length > 0 || oldAnswers.length > 0) && activeAnalysisPanelTab ? styles.chatRootWithPanel : styles.chatRoot}>
                <div
                    className={`${(answers.length > 0 || oldAnswers.length > 0) && activeAnalysisPanelTab ? styles.chatContainerWrapper : ""} ${activeAnalysisPanelTab && mobileShowPanel ? styles.mobileChatHidden : ""}`}
                >
                    {activeAnalysisPanelTab && !mobileShowPanel && (
                        <button className={styles.mobileForwardButton} onClick={() => setMobileShowPanel(true)} aria-label="View supporting content">
                            <img src={doubleChevronLeft} alt="" className={styles.mobileNavIcon} style={{ transform: "rotate(180deg)" }} />
                        </button>
                    )}
                    <div
                        ref={chatContainerRef}
                        data-chat-scroll
                        className={
                            (answers.length > 0 || oldAnswers.length > 0) && activeAnalysisPanelTab
                                ? styles.chatContainerWithPanel
                                : answers.length > 0 || oldAnswers.length > 0 || isLoading || isStreaming
                                  ? styles.chatScrollContainer
                                  : styles.chatContainer
                        }
                    >
                        {!lastQuestionRef.current ? (
                            <div className={styles.chatEmptyState}>
                                {/* <SparkleFilled fontSize={"120px"} primaryFill={"rgba(115, 118, 225, 1)"} aria-hidden="true" aria-label="Chat logo" /> */}
                                <div className={styles.chatEmptyStateTitleContainer}>
                                    <center className={styles.chatEmptyStateTitle}>AIDA</center>
                                    <center className={styles.chatEmptyStateSubTitle}>Artificial Intelligence for Development Analytics</center>
                                    <div className={styles.chatEmptyStateTagline}>Trusted evidence from UNDP evaluations, powered by AI</div>
                                </div>

                                <div className={styles.chatEmptyStateInput}>
                                    <QuestionInput
                                        clearOnSend
                                        placeholder={
                                            typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
                                                ? "Ask AIDA anything..."
                                                : "\u201CAsk AIDA anything about UNDP evaluations\u2014or explore an example. Use filters (evaluation type, year, region) to narrow your results.\u201D"
                                        }
                                        disabled={isLoading}
                                        onSend={question => makeApiRequest(question)}
                                        showSpeechInput={showSpeechInput}
                                        multiline
                                    />
                                </div>
                                <div ref={filterBarRef} className={styles.filterBarWrapper}>
                                    <div
                                        className={
                                            activeFilterBar ||
                                            region.length > 0 ||
                                            regionalBureau.length > 0 ||
                                            country.length > 0 ||
                                            evaluationType.length > 0 ||
                                            qarating.length > 0 ||
                                            sectionTypes.length > 0 ||
                                            gefEvaluation ||
                                            jointEvaluation ||
                                            yearFrom ||
                                            yearTo
                                                ? styles.chatEmptyStateSpacerOpen
                                                : styles.chatEmptyStateSpacer
                                        }
                                    >
                                        <div className={styles.filterButtonsRow}>
                                            <span className={styles.filterLabel}>FILTER</span>
                                            <button
                                                type="button"
                                                className={styles.filterLabelToggle}
                                                onClick={() => {
                                                    const isOpen = mobileFiltersExpanded || !!activeFilterBar;
                                                    if (isOpen) {
                                                        setMobileFiltersExpanded(false);
                                                        setActiveFilterBar(null);
                                                    } else {
                                                        setMobileFiltersExpanded(true);
                                                    }
                                                }}
                                            >
                                                FILTER
                                                {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
                                                <ChevronDown16Filled
                                                    className={`${styles.filterToggleIcon} ${mobileFiltersExpanded || activeFilterBar ? styles.filterToggleIconOpen : ""}`}
                                                />
                                            </button>
                                            <div
                                                className={`${styles.filterButtonsList} ${mobileFiltersExpanded || activeFilterBar ? styles.filterButtonsListOpen : ""}`}
                                            >
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "region" || region.length > 0
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "region" ? null : "region"));
                                                    }}
                                                >
                                                    Region{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "region" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("region")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "regionalBureau" || regionalBureau.length > 0
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "regionalBureau" ? null : "regionalBureau"));
                                                    }}
                                                >
                                                    Programme unit{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "regionalBureau" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("regionalBureau")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "country" || country.length > 0
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "country" ? null : "country"));
                                                    }}
                                                >
                                                    Country{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "country" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("country")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "evaluationType" || evaluationType.length > 0
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "evaluationType" ? null : "evaluationType"));
                                                    }}
                                                >
                                                    Evaluation type{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "evaluationType" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("evaluationType")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "sectionTypes" || sectionTypes.length > 0
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "sectionTypes" ? null : "sectionTypes"));
                                                    }}
                                                >
                                                    Section{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "sectionTypes" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("sectionTypes")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "yearFrom" || yearFrom ? styles.filterChipButtonActive : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "yearFrom" ? null : "yearFrom"));
                                                    }}
                                                >
                                                    Year from{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "yearFrom" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("yearFrom")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={activeFilterBar === "yearTo" || yearTo ? styles.filterChipButtonActive : styles.filterChipButton}
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "yearTo" ? null : "yearTo"));
                                                    }}
                                                >
                                                    Year to{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "yearTo" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("yearTo")}
                                                <span className={styles.filterDivider}></span>
                                            </div>
                                            <button
                                                className={moreFiltersExpanded || hasMoreFiltersActive || isMoreFilterOpen ? styles.moreFiltersButtonActive : styles.moreFiltersButton}
                                                type="button"
                                                onClick={() => {
                                                    setMoreFiltersExpanded(prev => !prev);
                                                    if (moreFiltersExpanded) setActiveFilterBar(null);
                                                }}
                                            >
                                                {moreFiltersExpanded || hasMoreFiltersActive || isMoreFilterOpen ? "...less" : "More..."}
                                            </button>
                                        </div>
                                        {(moreFiltersExpanded || hasMoreFiltersActive || isMoreFilterOpen) && (
                                            <div className={styles.moreFiltersRow}>
                                                <span className={styles.moreFiltersRowSpacer}>FILTER</span>
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "qarating" || qarating.length > 0
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "qarating" ? null : "qarating"));
                                                    }}
                                                >
                                                    Quality assessment rating{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "qarating" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("qarating")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "gefEvaluation" || gefEvaluation
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "gefEvaluation" ? null : "gefEvaluation"));
                                                    }}
                                                >
                                                    GEF evaluation{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "gefEvaluation" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("gefEvaluation")}
                                                <span className={styles.filterDivider}></span>
                                                <button
                                                    className={
                                                        activeFilterBar === "jointEvaluation" || jointEvaluation
                                                            ? styles.filterChipButtonActive
                                                            : styles.filterChipButton
                                                    }
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveFilterBar(prev => (prev === "jointEvaluation" ? null : "jointEvaluation"));
                                                    }}
                                                >
                                                    Joint{" "}
                                                    <ChevronDown16Filled
                                                        className={`${styles.filterArrowIcon} ${activeFilterBar === "jointEvaluation" ? styles.filterArrowIconOpen : ""}`}
                                                    />
                                                </button>
                                                {renderInlineDropdown("jointEvaluation")}
                                            </div>
                                        )}
                                    </div>
                                    {/* Selected filters chips - show in separate connected box when collapsed */}
                                    {!activeFilterBar &&
                                        (region.length > 0 ||
                                            regionalBureau.length > 0 ||
                                            country.length > 0 ||
                                            evaluationType.length > 0 ||
                                            qarating.length > 0 ||
                                            sectionTypes.length > 0 ||
                                            gefEvaluation ||
                                            jointEvaluation ||
                                            yearFrom ||
                                            yearTo) && (
                                            <div className={`${styles.selectedFiltersBox} ${mobileFiltersExpanded ? styles.selectedFiltersBoxOpen : ""}`}>
                                                <div className={styles.selectedFiltersRowInline}>
                                                    {region.map(r => {
                                                        const option = regionOptions.find(o => o.key === r);
                                                        return (
                                                            <div key={`region-${r}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || r}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("region", r)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {regionalBureau.map(rb => {
                                                        const option = regionalBureauOptions.find(o => o.key === rb);
                                                        return (
                                                            <div key={`rb-${rb}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || rb}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("regionalBureau", rb)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {country.map(c => {
                                                        const option = countryOptions.find(o => o.key === c);
                                                        return (
                                                            <div key={`country-${c}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || c}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("country", c)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {evaluationType.map(et => {
                                                        const option = evaluationTypeOptions.find(o => o.key === et);
                                                        return (
                                                            <div key={`et-${et}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || et}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("evaluationType", et)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {qarating.map(qa => {
                                                        const option = qaRatingOptions.find(o => o.key === qa);
                                                        return (
                                                            <div key={`qa-${qa}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || qa}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("qaRating", qa)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {sectionTypes.map(st => {
                                                        const option = sectionTypeOptions.find(o => o.key === st);
                                                        return (
                                                            <div key={`st-${st}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || st}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("sectionTypes", st)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {gefEvaluation && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>{gefEvaluation}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("gefEvaluation")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                    {jointEvaluation && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>Joint: {jointEvaluation}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("jointEvaluation")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                    {yearFrom && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>{yearFrom}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("yearFrom")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                    {yearTo && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>{yearTo}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("yearTo")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    {activeFilterBar && (
                                        <div
                                            key={activeFilterBar}
                                            className={`${styles.filterDropdown} ${
                                                activeFilterBar === "region" ||
                                                activeFilterBar === "evaluationType" ||
                                                activeFilterBar === "qarating" ||
                                                activeFilterBar === "sectionTypes"
                                                    ? styles.filterDropdown2Col
                                                    : activeFilterBar === "gefEvaluation" || activeFilterBar === "jointEvaluation"
                                                      ? styles.filterDropdown1Col
                                                      : activeFilterBar === "yearFrom" || activeFilterBar === "yearTo"
                                                        ? styles.filterDropdownYearCol
                                                        : styles.filterDropdown3Col
                                            }`}
                                        >
                                            {(activeFilterBar === "country" || activeFilterBar === "regionalBureau") && (
                                                <div className={styles.filterSearchWrapper}>
                                                    <input
                                                        ref={filterSearchInputRef}
                                                        type="text"
                                                        className={styles.filterSearchInput}
                                                        placeholder={`Search ${activeFilterBar === "country" ? "country" : "programme unit"}...`}
                                                        value={filterSearchText}
                                                        onChange={e => setFilterSearchText(e.target.value)}
                                                    />
                                                </div>
                                            )}
                                            {getFilteredOptions(activeFilterBar).map(option => {
                                                const current = getCurrentFilterValue(activeFilterBar);
                                                const isSelected = Array.isArray(current) ? current.includes(option.key as string) : current === option.key;
                                                return (
                                                    <button
                                                        key={option.key as string}
                                                        type="button"
                                                        className={`${styles.filterDropdownItem} ${isSelected ? styles.filterDropdownItemSelected : ""}`}
                                                        onClick={() => updateFilterValue(activeFilterBar, option.key as string)}
                                                    >
                                                        <span className={styles.filterDropdownItemCircle}></span>
                                                        <span className={styles.filterDropdownItemText} title={option.text}>
                                                            {option.text}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <ExampleList onExampleClicked={onExampleClicked} useGPT4V={useGPT4V} />
                            </div>
                        ) : (
                            <div className={activeAnalysisPanelTab ? styles.chatMessageStreamWithPanel : styles.chatMessageStream}>
                                {/* Render old answers first (from previous filter contexts) - always visible */}
                                {!isStreaming &&
                                    oldAnswers.map((answer, index) => (
                                        <div
                                            key={`old-${index}`}
                                            className={activeAnalysisPanelTab ? styles.chatMessageWrapperWithPanel : styles.chatMessageWrapper}
                                        >
                                            <UserChatMessage message={answer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <Answer
                                                    isStreaming={false}
                                                    key={`old-${index}`}
                                                    answer={answer[1]}
                                                    index={index} // Use original index for old answers
                                                    speechConfig={speechConfig}
                                                    isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                    onCitationClicked={c => onShowCitation(c, index)}
                                                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                    onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                    onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                    showFollowupQuestions={false} // Don't show followup questions for old answers
                                                    showSpeechOutputAzure={showSpeechOutputAzure}
                                                    showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                {/* Render streaming answers */}
                                {isStreaming &&
                                    streamedAnswers.map((streamedAnswer, index) => (
                                        <div
                                            key={`streamed-${index}`}
                                            className={activeAnalysisPanelTab ? styles.chatMessageWrapperWithPanel : styles.chatMessageWrapper}
                                        >
                                            <UserChatMessage message={streamedAnswer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <Answer
                                                    isStreaming={true}
                                                    key={`streamed-${index}`}
                                                    answer={streamedAnswer[1]}
                                                    index={oldAnswers.length + index}
                                                    speechConfig={speechConfig}
                                                    isSelected={false}
                                                    onCitationClicked={c => onShowCitation(c, oldAnswers.length + index)}
                                                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, oldAnswers.length + index)}
                                                    onSupportingContentClicked={() =>
                                                        onToggleTab(AnalysisPanelTabs.SupportingContentTab, oldAnswers.length + index)
                                                    }
                                                    onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                    showFollowupQuestions={useSuggestFollowupQuestions && streamedAnswers.length - 1 === index}
                                                    showSpeechOutputAzure={showSpeechOutputAzure}
                                                    showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                {/* Render current answers */}
                                {!isStreaming &&
                                    answers.map((answer, index) => (
                                        <div
                                            key={`current-${index}`}
                                            className={activeAnalysisPanelTab ? styles.chatMessageWrapperWithPanel : styles.chatMessageWrapper}
                                        >
                                            <UserChatMessage message={answer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <Answer
                                                    isStreaming={false}
                                                    key={`current-${index}`}
                                                    answer={answer[1]}
                                                    index={oldAnswers.length + index}
                                                    speechConfig={speechConfig}
                                                    isSelected={selectedAnswer === oldAnswers.length + index && activeAnalysisPanelTab !== undefined}
                                                    onCitationClicked={c => onShowCitation(c, oldAnswers.length + index)}
                                                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, oldAnswers.length + index)}
                                                    onSupportingContentClicked={() =>
                                                        onToggleTab(AnalysisPanelTabs.SupportingContentTab, oldAnswers.length + index)
                                                    }
                                                    onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                    showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                    showSpeechOutputAzure={showSpeechOutputAzure}
                                                    showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                {isLoading && (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerLoading />
                                        </div>
                                    </>
                                )}
                                {error ? (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                        </div>
                                    </>
                                ) : null}
                                <div ref={chatMessageStreamEnd} />
                            </div>
                        )}

                        {lastQuestionRef.current && (
                            <div data-sticky-input className={activeAnalysisPanelTab ? styles.chatInputSectionWithPanel : styles.chatInputSection}>
                                <div className={activeAnalysisPanelTab ? styles.chatInputBoxWrapperWithPanel : styles.chatInputBoxWrapper}>
                                    <div className={styles.chatInputActions}>
                                        {showChatScrollButton && (
                                            <button
                                                onClick={scrollToBottom}
                                                className={styles.scrollToBottomButtonFixed}
                                                title="Scroll to bottom"
                                                aria-label="Scroll to bottom"
                                            >
                                                <img src={sendIcon} alt="Scroll to bottom" className={styles.scrollIconFixed} />
                                            </button>
                                        )}
                                        <button onClick={clearChat} className={styles.clearChatButton} title="Clear chat" aria-label="Clear chat">
                                            <Icon iconName="Delete" className={styles.clearChatIcon} />
                                            <span className={styles.clearChatText}>Clear chat</span>
                                        </button>
                                    </div>
                                    <div className={activeAnalysisPanelTab ? styles.chatInputBoxWithPanel : styles.chatInputBox}>
                                        <QuestionInput
                                            clearOnSend
                                            placeholder="Ask anything..."
                                            disabled={isLoading}
                                            onSend={question => makeApiRequest(question)}
                                            showSpeechInput={showSpeechInput}
                                            multiline
                                            expandUp
                                        />
                                    </div>
                                </div>
                                <div ref={filterBarRef} className={activeAnalysisPanelTab ? styles.filterBarWrapperWithPanel : styles.filterBarWrapper}>
                                    <div
                                        className={
                                            activeAnalysisPanelTab
                                                ? activeFilterBar ||
                                                  region.length > 0 ||
                                                  regionalBureau.length > 0 ||
                                                  country.length > 0 ||
                                                  evaluationType.length > 0 ||
                                                  qarating.length > 0 ||
                                                  sectionTypes.length > 0 ||
                                                  gefEvaluation ||
                                                  jointEvaluation ||
                                                  yearFrom ||
                                                  yearTo
                                                    ? styles.chatInputFilterBarOpenWithPanel
                                                    : styles.chatInputFilterBarWithPanel
                                                : activeFilterBar ||
                                                    region.length > 0 ||
                                                    regionalBureau.length > 0 ||
                                                    country.length > 0 ||
                                                    evaluationType.length > 0 ||
                                                    qarating.length > 0 ||
                                                    sectionTypes.length > 0 ||
                                                    gefEvaluation ||
                                                    jointEvaluation ||
                                                    yearFrom ||
                                                    yearTo
                                                  ? styles.chatInputFilterBarOpen
                                                  : styles.chatInputFilterBar
                                        }
                                    >
                                        <div className={activeAnalysisPanelTab ? styles.filterButtonsRowWithPanel : styles.filterButtonsRow}>
                                            <span className={styles.filterLabel}>FILTER</span>
                                            <button
                                                type="button"
                                                className={styles.filterLabelToggle}
                                                onClick={() => {
                                                    const isOpen = mobileFiltersExpanded || !!activeFilterBar;
                                                    if (isOpen) {
                                                        setMobileFiltersExpanded(false);
                                                        setActiveFilterBar(null);
                                                    } else {
                                                        setMobileFiltersExpanded(true);
                                                    }
                                                }}
                                            >
                                                FILTER
                                                {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
                                                <ChevronDown16Filled
                                                    className={`${styles.filterToggleIcon} ${mobileFiltersExpanded || activeFilterBar ? styles.filterToggleIconOpen : ""}`}
                                                />
                                            </button>
                                            <div
                                                className={`${activeAnalysisPanelTab ? styles.filterButtonsWrapperWithPanel : styles.filterButtonsWrapper} ${mobileFiltersExpanded || activeFilterBar ? styles.filterButtonsListOpen : ""}`}
                                            >
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "region" || region.length > 0
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "region" ? null : "region")}
                                                    >
                                                        Region{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "region" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("region")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "regionalBureau" || regionalBureau.length > 0
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "regionalBureau" ? null : "regionalBureau")}
                                                    >
                                                        Programme unit{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "regionalBureau" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("regionalBureau")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "country" || country.length > 0
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "country" ? null : "country")}
                                                    >
                                                        Country{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "country" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("country")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "evaluationType" || evaluationType.length > 0
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "evaluationType" ? null : "evaluationType")}
                                                    >
                                                        Evaluation type{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "evaluationType" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("evaluationType")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "sectionTypes" || sectionTypes.length > 0
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "sectionTypes" ? null : "sectionTypes")}
                                                    >
                                                        Section{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "sectionTypes" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("sectionTypes")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "yearFrom" || yearFrom ? styles.filterChipButtonActive : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "yearFrom" ? null : "yearFrom")}
                                                    >
                                                        Year from{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "yearFrom" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("yearFrom")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "yearTo" || yearTo ? styles.filterChipButtonActive : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "yearTo" ? null : "yearTo")}
                                                    >
                                                        Year to{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "yearTo" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("yearTo")}
                                                </span>
                                                <span className={styles.filterDivider}></span>
                                            </div>
                                            <button
                                                className={moreFiltersExpanded || hasMoreFiltersActive || isMoreFilterOpen ? styles.moreFiltersButtonActive : styles.moreFiltersButton}
                                                type="button"
                                                onClick={() => {
                                                    setMoreFiltersExpanded(prev => !prev);
                                                    if (moreFiltersExpanded) setActiveFilterBar(null);
                                                }}
                                            >
                                                {moreFiltersExpanded || hasMoreFiltersActive || isMoreFilterOpen ? "...less" : "More..."}
                                            </button>
                                        </div>
                                        {(moreFiltersExpanded || hasMoreFiltersActive || isMoreFilterOpen) && (
                                            <div className={styles.moreFiltersRowWithPanel}>
                                                <span className={styles.moreFiltersRowSpacer}>FILTER</span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "qarating" || qarating.length > 0
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "qarating" ? null : "qarating")}
                                                    >
                                                        Quality assessment rating{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "qarating" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("qarating")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "gefEvaluation" || gefEvaluation
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "gefEvaluation" ? null : "gefEvaluation")}
                                                    >
                                                        GEF evaluation{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "gefEvaluation" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("gefEvaluation")}
                                                </span>
                                                <span className={styles.filterItemGroup}>
                                                    <span className={styles.filterDivider}></span>
                                                    <button
                                                        className={
                                                            activeFilterBar === "jointEvaluation" || jointEvaluation
                                                                ? styles.filterChipButtonActive
                                                                : styles.filterChipButton
                                                        }
                                                        type="button"
                                                        onClick={() => setActiveFilterBar(activeFilterBar === "jointEvaluation" ? null : "jointEvaluation")}
                                                    >
                                                        Joint{" "}
                                                        <ChevronDown16Filled
                                                            className={`${styles.filterArrowIcon} ${activeFilterBar === "jointEvaluation" ? styles.filterArrowIconOpen : ""}`}
                                                        />
                                                    </button>
                                                    {renderInlineDropdown("jointEvaluation")}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {/* Selected filters chips - show in separate connected box when collapsed */}
                                    {!activeFilterBar &&
                                        (region.length > 0 ||
                                            regionalBureau.length > 0 ||
                                            country.length > 0 ||
                                            evaluationType.length > 0 ||
                                            qarating.length > 0 ||
                                            sectionTypes.length > 0 ||
                                            gefEvaluation ||
                                            jointEvaluation ||
                                            yearFrom ||
                                            yearTo) && (
                                            <div
                                                className={`${activeAnalysisPanelTab ? styles.selectedFiltersBoxWithPanel : styles.selectedFiltersBox} ${mobileFiltersExpanded ? styles.selectedFiltersBoxOpen : ""}`}
                                            >
                                                <div className={styles.selectedFiltersRowInline}>
                                                    {region.map(r => {
                                                        const option = regionOptions.find(o => o.key === r);
                                                        return (
                                                            <div key={`region-${r}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || r}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("region", r)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {regionalBureau.map(rb => {
                                                        const option = regionalBureauOptions.find(o => o.key === rb);
                                                        return (
                                                            <div key={`rb-${rb}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || rb}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("regionalBureau", rb)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {country.map(c => {
                                                        const option = countryOptions.find(o => o.key === c);
                                                        return (
                                                            <div key={`country-${c}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || c}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("country", c)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {evaluationType.map(et => {
                                                        const option = evaluationTypeOptions.find(o => o.key === et);
                                                        return (
                                                            <div key={`et-${et}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || et}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("evaluationType", et)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {qarating.map(qa => {
                                                        const option = qaRatingOptions.find(o => o.key === qa);
                                                        return (
                                                            <div key={`qa-${qa}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || qa}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("qaRating", qa)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {sectionTypes.map(st => {
                                                        const option = sectionTypeOptions.find(o => o.key === st);
                                                        return (
                                                            <div key={`st-${st}`} className={styles.selectedFilterChipInline}>
                                                                <span className={styles.selectedFilterChipText}>{option?.text || st}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.selectedFilterChipRemove}
                                                                    onClick={() => handleRemoveFilter("sectionTypes", st)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    {gefEvaluation && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>{gefEvaluation}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("gefEvaluation")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                    {jointEvaluation && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>Joint: {jointEvaluation}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("jointEvaluation")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                    {yearFrom && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>{yearFrom}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("yearFrom")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                    {yearTo && (
                                                        <div className={styles.selectedFilterChipInline}>
                                                            <span className={styles.selectedFilterChipText}>{yearTo}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.selectedFilterChipRemove}
                                                                onClick={() => handleRemoveFilter("yearTo")}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    {activeFilterBar && (
                                        <div
                                            key={activeFilterBar}
                                            className={`${activeAnalysisPanelTab ? styles.filterDropdownWithPanel : styles.filterDropdown} ${
                                                activeFilterBar === "region" ||
                                                activeFilterBar === "evaluationType" ||
                                                activeFilterBar === "qarating" ||
                                                activeFilterBar === "sectionTypes"
                                                    ? styles.filterDropdown2Col
                                                    : activeFilterBar === "gefEvaluation" || activeFilterBar === "jointEvaluation"
                                                      ? styles.filterDropdown1Col
                                                      : activeFilterBar === "yearFrom" || activeFilterBar === "yearTo"
                                                        ? styles.filterDropdownYearCol
                                                        : styles.filterDropdown3Col
                                            }`}
                                        >
                                            {(activeFilterBar === "country" || activeFilterBar === "regionalBureau") && (
                                                <div className={styles.filterSearchWrapper}>
                                                    <input
                                                        ref={filterSearchInputRef}
                                                        type="text"
                                                        className={styles.filterSearchInput}
                                                        placeholder={`Search ${activeFilterBar === "country" ? "country" : "programme unit"}...`}
                                                        value={filterSearchText}
                                                        onChange={e => setFilterSearchText(e.target.value)}
                                                    />
                                                </div>
                                            )}
                                            {getFilteredOptions(activeFilterBar).map(option => {
                                                const current = getCurrentFilterValue(activeFilterBar);
                                                const isSelected = Array.isArray(current) ? current.includes(option.key as string) : current === option.key;
                                                return (
                                                    <button
                                                        key={option.key as string}
                                                        type="button"
                                                        className={`${styles.filterDropdownItem} ${isSelected ? styles.filterDropdownItemSelected : ""}`}
                                                        onClick={() => updateFilterValue(activeFilterBar, option.key as string)}
                                                    >
                                                        <span className={styles.filterDropdownItemCircle}></span>
                                                        <span className={styles.filterDropdownItemText} title={option.text}>
                                                            {option.text}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {(answers.length > 0 || oldAnswers.length > 0) && activeAnalysisPanelTab && <div className={styles.chatContainerSpacer}></div>}
                </div>

                {(answers.length > 0 || oldAnswers.length > 0) &&
                    activeAnalysisPanelTab &&
                    (selectedAnswer < oldAnswers.length ? oldAnswers[selectedAnswer]?.[1] : answers[selectedAnswer - oldAnswers.length]?.[1]) && (
                        <div className={`${styles.analysisPanelWrapper} ${!mobileShowPanel ? styles.mobilePanelHidden : ""}`}>
                            <button className={styles.mobileBackButton} onClick={() => setMobileShowPanel(false)} aria-label="Back to chat">
                                <img src={doubleChevronLeft} alt="" className={styles.mobileNavIcon} />
                            </button>
                            <AnalysisPanel
                                className={styles.chatAnalysisPanel}
                                activeCitation={activeCitation}
                                onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                                citationHeight="750px"
                                answer={selectedAnswer < oldAnswers.length ? oldAnswers[selectedAnswer][1] : answers[selectedAnswer - oldAnswers.length][1]}
                                activeTab={activeAnalysisPanelTab}
                                onClose={() => setActiveAnalysisPanelTab(undefined)}
                            />
                            {activeAnalysisPanelTab === AnalysisPanelTabs.SupportingContentTab && (
                                <div className={styles.scrollButtonFooterRight} style={{ visibility: showPanelScrollButton ? "visible" : "hidden" }}>
                                    <button
                                        onClick={() => {
                                            const panel = document.querySelector(`.${styles.chatAnalysisPanel}`);
                                            if (panel) panel.scrollTo({ top: panel.scrollHeight, behavior: "smooth" });
                                        }}
                                        className={styles.scrollToBottomButtonRight}
                                        title="Scroll to bottom"
                                        aria-label="Scroll to bottom"
                                    >
                                        <img src={sendIcon} alt="Scroll to bottom" className={styles.scrollIconRight} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                {((useLogin && showChatHistoryCosmos) || showChatHistoryBrowser) && (
                    <HistoryPanel
                        provider={historyProvider}
                        isOpen={isHistoryPanelOpen}
                        notify={!isStreaming && !isLoading}
                        onClose={() => setIsHistoryPanelOpen(false)}
                        onChatSelected={answers => {
                            if (answers.length === 0) return;
                            setAnswers(answers);
                            lastQuestionRef.current = answers[answers.length - 1][0];
                        }}
                    />
                )}

                {/* <Panel
                    headerText={t("labels.headerText")}
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => {
                        setIsConfigPanelOpen(false);
                        setFilterScrollTarget(undefined);
                    }}
                    closeButtonAriaLabel={t("labels.closeButton")}
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>{t("labels.closeButton")}</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <Settings
                        promptTemplate={promptTemplate}
                        temperature={temperature}
                        retrieveCount={retrieveCount}
                        maxSubqueryCount={maxSubqueryCount}
                        resultsMergeStrategy={resultsMergeStrategy}
                        seed={seed}
                        minimumSearchScore={minimumSearchScore}
                        minimumRerankerScore={minimumRerankerScore}
                        useSemanticRanker={useSemanticRanker}
                        useSemanticCaptions={useSemanticCaptions}
                        useQueryRewriting={useQueryRewriting}
                        reasoningEffort={reasoningEffort}
                        excludeCategory={excludeCategory}
                        includeCategory={includeCategory}
                        retrievalMode={retrievalMode}
                        useGPT4V={useGPT4V}
                        gpt4vInput={gpt4vInput}
                        vectorFields={vectorFields}
                        showSemanticRankerOption={showSemanticRankerOption}
                        showQueryRewritingOption={showQueryRewritingOption}
                        showReasoningEffortOption={showReasoningEffortOption}
                        showGPT4VOptions={showGPT4VOptions}
                        showVectorOption={showVectorOption}
                        useOidSecurityFilter={useOidSecurityFilter}
                        useGroupsSecurityFilter={useGroupsSecurityFilter}
                        useLogin={!!useLogin}
                        loggedIn={loggedIn}
                        requireAccessControl={requireAccessControl}
                        shouldStream={shouldStream}
                        streamingEnabled={streamingEnabled}
                        useSuggestFollowupQuestions={useSuggestFollowupQuestions}
                        showSuggestFollowupQuestions={true}
                        showAgenticRetrievalOption={showAgenticRetrievalOption}
                        useAgenticRetrieval={useAgenticRetrieval}
                        onChange={handleSettingsChange}
                    />
                    {useLogin && <TokenClaimsDisplay />}
                </Panel> */}

                <Panel
                    headerText={t("filters.headerText")}
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel={t("filters.closeButton")}
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>{t("filters.closeButton")}</DefaultButton>}
                    isFooterAtBottom={true}
                    headerClassName={styles.filterPanelHeader}
                >
                    <Filters
                        country={country}
                        region={region}
                        regionalBureau={regionalBureau}
                        evaluationType={evaluationType}
                        qaRating={qarating}
                        sectionTypes={sectionTypes}
                        projectType={projectType}
                        sdgGoals={sdgGoals}
                        jointEvaluation={jointEvaluation}
                        gefEvaluation={gefEvaluation}
                        yearFrom={yearFrom}
                        yearTo={yearTo}
                        onChange={handleFiltersChange}
                        onReset={handleFiltersReset}
                        scrollToId={filterScrollTarget}
                    />
                    {useLogin && <TokenClaimsDisplay />}
                </Panel>

                <Modal isOpen={isWelcomeModalOpen} onDismiss={handleWelcomeModalClose} isBlocking={true} isModeless={false} className={styles.welcomeModal}>
                    <div className={styles.welcomeModalBackdrop} onClick={handleWelcomeModalClose}>
                        <div className={styles.welcomeModalContent} onClick={e => e.stopPropagation()}>
                            <div className={styles.welcomeModalHeader}>
                                <img src={welcomeIcon} alt="" className={styles.welcomeIcon} />
                                <h2>Welcome to the New AIDA!</h2>
                            </div>
                            <div className={styles.welcomeModalBody}>
                                <p>As of 30 June 2025, AIDA has a smarter, chatbot-based interface that makes finding what you need faster and easier.</p>
                            </div>
                            <div className={styles.welcomeModalFooter}>
                                <DefaultButton onClick={handleWelcomeModalClose} primary>
                                    Get Started
                                </DefaultButton>
                            </div>
                        </div>
                    </div>
                </Modal>
            </main>
        </div>
    );
};

export default Chat;
